import { Link } from "react-router-dom";
import Seo, { SITE_URL } from "@/components/Seo";
import PageLayout from "@/components/layout/PageLayout";
import { methodSteps, glossary } from "@/mocks/content";

const pitfalls = [
  {
    icon: "ri-scissors-cut-line",
    title: "断章取义",
    desc: "只截取对立场有利的片段，隐去上下文。对策是补全前后文与时间点再下判断。",
  },
  {
    icon: "ri-price-tag-3-line",
    title: "标签先行",
    desc: "先把对方贴上一个道德标签，再为标签找证据，论证顺序就反了。",
  },
  {
    icon: "ri-mental-health-line",
    title: "情绪动员",
    desc: "用愤怒、怜悯等情绪代替事实论证。看到强情绪，先停一步问证据在哪。",
  },
  {
    icon: "ri-fingerprint-line",
    title: "假权威",
    desc: "编造并不存在的来源、机构或头衔为说法背书。请回头核验出处是否真实。",
  },
  {
    icon: "ri-volume-mute-line",
    title: "利用沉默",
    desc: "把「未回应」直接解读为「默认有罪」。沉默不等于承认，也请给对方回应的时间。",
  },
];

const methodologySchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: SITE_URL + "/" },
        { "@type": "ListItem", position: 2, name: "吃瓜方法论", item: SITE_URL + "/methodology" },
      ],
    },
    {
      "@type": "Article",
      headline: "吃透一个瓜的四条路线：吃瓜方法论",
      description: "一套可复用的信息整理方法：找源头、建时间线、辨立场、溯源查证，并附识瓜词典与防误导指南。",
      author: { "@type": "Organization", name: "吃瓜路线" },
      publisher: { "@type": "Organization", name: "吃瓜路线" },
      mainEntityOfPage: SITE_URL + "/methodology",
    },
  ],
};

export default function Methodology() {
  return (
    <PageLayout>
      <Seo
        title="吃瓜方法论：吃透一个瓜的四条路线 - 吃瓜路线"
        description="吃瓜路线的理性吃瓜方法论：找源头、建时间线、辨立场、溯源查证四步整理法，配识瓜词典与五种需警惕的误导手法，帮你不再被带偏。"
        keywords="吃瓜方法论,信息整理,线索串联,识瓜词典,防误导指南,理性吃瓜"
        path="/methodology"
        schema={methodologySchema}
      />

      {/* 面包屑 */}
      <nav aria-label="面包屑" className="border-b border-background-200 bg-background-100">
        <ol className="mx-auto flex w-full max-w-7xl items-center gap-2 px-4 py-3 text-xs text-foreground-500 md:px-6">
          <li>
            <Link to="/" className="hover:text-primary-700">
              首页
            </Link>
          </li>
          <li className="text-foreground-300">/</li>
          <li aria-current="page" className="font-medium text-foreground-700">
            吃瓜方法论
          </li>
        </ol>
      </nav>

      {/* Hero */}
      <section className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent-700">
          METHODOLOGY · 方法论
        </p>
        <div className="mt-3 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <h1 className="font-heading text-3xl font-black leading-tight text-foreground-950 md:text-4xl">
              吃透一个瓜的四条路线
            </h1>
            <p className="mt-4 text-base leading-relaxed text-foreground-700">
              围观本身没有错，错误的是「只看情绪、不看结构」。这套方法把「吃瓜」变成一项可以被训练的信息整理能力——找源头、建时间线、辨立场、溯源查证，四步走完，结论自然清晰。
            </p>
          </div>
          <div className="grid shrink-0 grid-cols-2 gap-3 lg:w-80">
            {[
              { n: "04", t: "步标准路线" },
              { n: "05", t: "类常见误导" },
              { n: "08", t: "个识瓜热词" },
              { n: "100%", t: "可迁移的思维" },
            ].map((s) => (
              <div
                key={s.t}
                className="rounded-lg border border-background-200 bg-background-100 px-4 py-3 text-center"
              >
                <div className="font-heading text-2xl font-black text-primary-700">{s.n}</div>
                <div className="mt-0.5 text-xs text-foreground-500">{s.t}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 四条路线 */}
      <section className="bg-background-100">
        <div className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6 md:py-16">
          <h2 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
            一套四步的标准路线
          </h2>
          <div className="mt-8 space-y-5">
            {methodSteps.map((s) => (
              <article
                key={s.step}
                className="grid grid-cols-1 gap-5 rounded-lg border border-background-200 bg-background-50 p-6 md:grid-cols-[80px_1fr_1fr] md:items-center md:p-8"
              >
                <div className="flex md:flex-col md:items-start">
                  <span className="flex h-12 w-12 items-center justify-center rounded-md bg-primary-500 text-background-50">
                    <i className={`${s.icon} text-xl`} aria-hidden="true"></i>
                  </span>
                  <span className="mt-3 block font-heading text-2xl font-black text-primary-200 md:text-4xl">
                    {s.step}
                  </span>
                </div>
                <div>
                  <h3 className="font-heading text-xl font-bold text-foreground-900 md:text-2xl">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600 md:text-base">
                    {s.desc}
                  </p>
                </div>
                <div className="md:justify-self-end">
                  <span className="inline-flex items-center gap-2 rounded-full bg-secondary-100 px-4 py-2 text-sm text-secondary-900">
                    <i className="ri-todo-line text-accent-700" aria-hidden="true"></i>
                    {s.action}
                  </span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* 防误导 */}
      <section className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent-700">
              防误导指南 · WATCH OUT
            </p>
            <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
              五种需要警惕的手法
            </h2>
          </div>
          <p className="max-w-md text-sm text-foreground-600">
            识别这些套路，是「不被带偏」的最低门槛。每一条背后都对应一个具体的自查动作。
          </p>
        </div>
        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {pitfalls.map((p) => (
            <div
              key={p.title}
              className="rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-200"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                <i className={`${p.icon} text-lg`} aria-hidden="true"></i>
              </span>
              <h3 className="mt-3 text-sm font-bold text-foreground-900">{p.title}</h3>
              <p className="mt-2 text-xs leading-relaxed text-foreground-600">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 词典 */}
      <section className="bg-background-100">
        <div className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6 md:py-16">
          <h2 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
            识瓜词典：看懂讨论的基本词
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-foreground-600">
            一些高频词与网络语，理解它们能减少大量误解。以下释义以理性、克制的口径给出。
          </p>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {glossary.map((g) => (
              <div
                key={g.word}
                className="rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <div className="flex items-center gap-2">
                  <span className="font-heading text-lg font-bold text-foreground-900">{g.word}</span>
                  <span className="rounded-full bg-accent-100 px-2 py-0.5 text-[10px] text-accent-800">
                    {g.type}
                  </span>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{g.def}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="flex flex-col items-center justify-between gap-5 rounded-lg bg-gradient-to-r from-primary-600 to-accent-500 p-8 text-center text-background-50 md:flex-row md:p-10 md:text-left">
          <div>
            <h2 className="font-heading text-2xl font-black md:text-3xl">方法学完了，去实战理清一件事</h2>
            <p className="mt-2 max-w-xl text-sm text-background-50/85">
              打开一份事件地图，用刚学到的时间线、观点光谱与溯源清单，检验一下这套方法是否真的让你看得更清楚。
            </p>
          </div>
          <Link
            to="/events"
            className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md bg-background-50 px-6 py-3 text-sm font-semibold text-primary-700 hover:bg-accent-50"
          >
            去围观事件地图
            <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        </div>
      </section>
    </PageLayout>
  );
}